import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, FileText, Download, CheckCircle, Calendar, Zap, FlaskConical } from 'lucide-react';

export const CourseCard = ({ course, hasAccess, onView, onQuickSchedule, relevanceNote, onTagClick }) => {
  const { t } = useTranslation();
  const levelColors = {
    'Beginner': 'bg-green-100 text-green-700',
    'Intermediate': 'bg-yellow-100 text-yellow-700',
    'Advanced': 'bg-red-100 text-red-700'
  };

  const formatDownloadDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isNew = course.created_at &&
    (Date.now() - new Date(course.created_at).getTime()) < 30 * 24 * 60 * 60 * 1000;

  const downloadDate = formatDownloadDate(course.last_downloaded);
  const showQuickSchedule = course.last_downloaded && !course.has_workshop && onQuickSchedule;

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden card-hover" data-testid={`course-card-${course.id}`}>
      <div className="h-2 bg-levelup-dark"></div>
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${levelColors[course.level] || 'bg-gray-100 text-gray-700'}`}>
              {course.level}
            </span>
            {isNew && (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-levelup-accent text-levelup-darker tracking-wide" data-testid={`new-badge-${course.id}`}>
                NEW
              </span>
            )}
            {course.hands_on_lab && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200" data-testid={`lab-badge-${course.id}`}>
                <FlaskConical size={11} />Lab
              </span>
            )}
          </div>
          {hasAccess && <CheckCircle className="text-green-500 flex-shrink-0" size={20} />}
        </div>
        <h3 className="font-semibold text-levelup-dark mb-2 line-clamp-2">{course.title}</h3>
        <p className="text-sm text-gray-500 mb-4 line-clamp-2">{course.description}</p>
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="px-2 py-1 bg-levelup-dark/10 text-levelup-dark rounded text-xs">{course.solution_area}</span>
          <span className="px-2 py-1 bg-levelup-accent/20 text-levelup-darker rounded text-xs">{course.course_type}</span>
        </div>
        {/* Search Tags */}
        {(course.search_tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(course.search_tags || []).slice(0, 3).map(tag => (
              <button
                key={tag}
                type="button"
                onClick={(e) => { e.stopPropagation(); onTagClick && onTagClick(tag); }}
                className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 hover:border-teal-400 transition-colors font-medium"
                data-testid={`tag-${course.id}-${tag}`}
                title={`Filter by tag: ${tag}`}
              >
                #{tag}
              </button>
            ))}
            {(course.search_tags || []).length > 3 && (
              <span className="text-xs text-gray-400 flex items-center">+{(course.search_tags || []).length - 3} more</span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
          <span className="flex items-center"><Clock size={14} className="mr-1" />{course.duration}</span>
          <span className="flex items-center"><FileText size={14} className="mr-1" />{course.files?.length || 0} {t('courses.files')}</span>
        </div>
        {downloadDate && (
          <div className="flex items-center text-xs text-green-600 bg-green-50 rounded-lg px-3 py-2 mb-3" data-testid={`download-date-${course.id}`}>
            <Download size={12} className="mr-1.5" />
            <span>{t('courses.downloadedOn')}: {downloadDate}</span>
          </div>
        )}
        {relevanceNote && (
          <div className="flex items-start text-xs text-levelup-accent bg-levelup-accent/10 rounded-lg px-3 py-2 mb-3" data-testid={`relevance-note-${course.id}`}>
            <Zap size={12} className="mr-1.5 mt-0.5 flex-shrink-0" />
            <span>{relevanceNote}</span>
          </div>
        )}
        {showQuickSchedule && (
          <button onClick={(e) => { e.stopPropagation(); onQuickSchedule(course); }}
            className="w-full bg-levelup-accent text-levelup-darker py-2 rounded-lg font-medium hover:bg-levelup-accent-hover transition-colors mb-2 flex items-center justify-center"
            data-testid={`quick-schedule-${course.id}`}>
            <Calendar size={16} className="mr-2" />
            {t('courses.quickSchedule')}
          </button>
        )}
        <button onClick={onView}
          className="w-full bg-levelup-dark text-white py-2 rounded-lg font-medium hover:bg-levelup-darker transition-colors"
          data-testid={`view-course-${course.id}`}>
          {t('common.viewDetails')}
        </button>
      </div>
    </div>
  );
};
